import React, {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'astroturf/react';
import { FeedContext } from 'js/social/contexts/feed';
import ModalBase from 'js/components/modals/Modal';
import { Banner } from 'js/design_system/Banner';
import { DeprecatedBreakpoint } from 'js/design_system/DeprecatedBreakpoint';
import { Button } from 'js/design_system/Button';
import { TextButton } from 'js/design_system/TextButton';
import { Colors } from 'js/design_system/Color';
import { Heading as HeadingBase } from 'js/design_system/Heading';
import CloseIcon from 'js/design_system/icons/close.svg';
import { ImageIcon, InfoIcon, PaperIcon } from 'js/social/shared/Icons';
import UserCard, { User as Author } from 'js/social/UserCard';
import {
  SocialEditor,
  SocialEditorHandle,
} from 'js/social/shared/SocialEditor/SocialEditor';
import {
  FollowableUser,
  ResourcePayload,
  ResourceType,
  Tag,
  Image,
  Paper,
} from 'js/social/shared/types';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import {
  getImpressionPage,
  getSocialImpressionSource,
} from 'js/social/ImpressionHelper';
import {
  AttachmentStatus,
  useAttachment,
} from 'js/social/shared/attachments/useAttachment';
import { usePaperLinkDetection } from 'js/social/shared/attachments/usePaperLinkDetection';
import useBreakpoint from 'js/utils/hooks/useDeprecatedBreakpoint';

const Modal = styled(ModalBase)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: hidden;
  padding: 16px;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;

  @include for-tablet-up {
    padding: 32px;
    width: 60vw;
    height: auto;
  }
`;

const HeadingGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @include for-tablet-up {
    margin: -16px 0;
  }
`;

const Heading = styled(HeadingBase)`
  font-size: 22px;
  line-height: 28px;
`;

const CloseButton = styled(TextButton)`
  height: 48px;
  width: 48px;
  margin-right: -12px;
  padding: 0;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-grow: 1;
  overflow-y: auto;
`;

const SocialEditorContainer = styled.div`
  font: 400 16px/20px Roboto;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ButtonGroup = styled.div<{ spaced?: boolean }>`
  display: flex;
  align-items: center;

  &.spaced {
    justify-content: space-between;
  }

  @include for-tablet-up {
    gap: 8px;
  }
`;

const ActionButton = styled(TextButton)`
  font-size: 14px;
  height: 32px;
`;

const PrimaryButton = styled(Button)`
  font-size: 14px;
  height: 32px;
`;

const AttachButton = styled(ActionButton)`
  padding: 0 12px;

  &:first-child {
    padding-left: 4px;
  }
`;

const Divider = styled.div`
  width: 100%;
  border-bottom: 1px solid $ds-gray-light;
`;

const loadDraft = (
  id: string,
  getUserData: (id: number) => FollowableUser,
): { content: string; tags: Tag[] } => {
  const draft = window.sessionStorage.getItem(id);
  if (draft != null) {
    try {
      const parsedDraft = JSON.parse(draft);
      // Attempt to get the user data for each tagged user. If it fails, just clear the draft.
      parsedDraft.tags.forEach((tag: Tag) => {
        getUserData(tag.tagId);
      });
      return parsedDraft;
    } catch {
      window.sessionStorage.removeItem(id);
    }
  }
  return { content: '', tags: [] };
};

type Props = {
  /**
   * Some form of identifier for the social item.
   * Used for saving drafts in sessionStorage.
   */
  id: string;
  /**
   * Depending on the resource type, different options will appear
   * in the modal, e.g. images.
   */
  type: ResourceType;
  /**
   * Info about the current user, used to show their
   * name, image, and affiliation.
   */
  author: Author;
  /** Copy for the modal heading */
  heading: string;
  /** Copy for the text area input */
  placeholder?: string;
  /** Copy for the primary CTA button. */
  cta: string;
  /** Anything to prefill the input box with */
  initialContent?: string;
  /** Anything to prefill the tags with */
  initialTags?: Tag[];
  /** Any image that comes pre-attached */
  initialImage?: Image;
  /** Any paper that comes pre-attached */
  initialPaper?: Paper;
  /** If an attachment is present, can it be removed? */
  attachmentRemovable?: boolean;
  /** Node to be displayed below the CTA buttons. */
  footer?: React.ReactNode;
  /** Whether or not some form of content is required for submission. */
  inputRequired?: boolean;
  /** Optionally disable tagging for this item. */
  disableTags?: boolean;
  /** Optionally disable hashtags for this item. */
  disableHashtags?: boolean;
  /** Optionally disable images for this item. */
  disableImages?: boolean;
  /** Optionally disable papers for this item. */
  disablePapers?: boolean;
  /**
   * Clicking outside the modal, clicking Cancel,
   * or clicking the close 'X' calls this function.
   */
  onClose: () => unknown;
  /** This function is called when a user submits their social item. */
  onSubmit: (payload: ResourcePayload) => unknown;
};

type ItemDraft = {
  content: string;
  tags: Tag[];
};

const CreateSocialItem: FC<React.PropsWithChildren<Props>> = ({
  id,
  type,
  author,
  heading,
  placeholder,
  cta,
  initialContent,
  initialTags,
  initialImage = undefined,
  initialPaper = undefined,
  inputRequired = true,
  disableTags = false,
  disableHashtags = false,
  disableImages = type !== 'post',
  disablePapers = type !== 'post',
  attachmentRemovable = true,
  onClose,
  onSubmit,
  footer,
  children,
}) => {
  const {
    feedConfig: { page },
    userActions: { getUserData },
  } = useContext(FeedContext);

  // Load draft
  const [startingDraftSelected, setStartingDraftSelected] = useState(false);
  let initialDraft: ItemDraft = { content: '', tags: [] };
  if (!startingDraftSelected) {
    initialDraft =
      initialContent === undefined && initialTags === undefined
        ? loadDraft(id, getUserData)
        : { content: initialContent || '', tags: initialTags || [] };
    setStartingDraftSelected(true);
  }

  const [content, setContent] = useState(initialDraft.content);
  const [tags, setTags] = useState<Tag[]>(initialDraft.tags);
  const [submitting, setSubmitting] = useState(false);
  const [prompt, setPrompt] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const socialEditorRef = useRef<SocialEditorHandle>(null);
  const mobile = useBreakpoint() < DeprecatedBreakpoint.tablet;

  const hasText = useMemo(
    () => !!(content.trim() || tags.length > 0),
    [content, tags],
  );

  // Attachments
  const {
    image,
    paper,
    content: attachmentContent,
    status: attachmentStatus,
    hasAttachment,
    onImageAttach,
    onPaperAttach,
    onPaperLinksChange,
  } = useAttachment(initialImage, initialPaper, attachmentRemovable);
  usePaperLinkDetection(
    content,
    disablePapers ? () => null : onPaperLinksChange,
  );

  // If this is a create post modal, wrap elements in ImpressionWatcher for click tracking
  const trackImpressions = page !== 'single_post' && type === ResourceType.Post;
  const IMPRESSION_PAGE = getImpressionPage(page);
  const IMPRESSION_SECTION = 'prompt';

  // Cancel prompt if the user removes the attachment
  useEffect(() => {
    if (prompt && !hasAttachment) {
      setPrompt(false);
    }
  }, [prompt, hasAttachment]);

  const { allowSubmit, loading } = useMemo(() => {
    const hasContent = !inputRequired || hasText || hasAttachment;
    const loading = attachmentStatus === AttachmentStatus.Loading || submitting;
    const allowSubmit = hasContent && !loading;
    return { allowSubmit, loading };
  }, [inputRequired, submitting, attachmentStatus, hasAttachment, hasText]);

  const submit = async () => {
    if (!allowSubmit) return;

    if (!prompt && !hasText && hasAttachment) {
      socialEditorRef.current?.focus();
      return setPrompt(true);
    }

    setSubmitting(true);
    try {
      await onSubmit({ content, tags, image, paper });
      window.sessionStorage.removeItem(id);
      onClose();
    } catch {
      setSubmitting(false);
    }
  };

  const saveDraft = useCallback(() => {
    window.sessionStorage.setItem(id, JSON.stringify({ content, tags }));
  }, [content, tags, id]);

  const closeModal = useCallback(() => {
    if (!loading && initialContent === undefined) {
      saveDraft();
      onClose();
    }
  }, [loading, onClose, initialContent, saveDraft]);

  const closeModalOnEscape = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    },
    [closeModal],
  );

  const handleAddImage = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.currentTarget.files?.length) {
        onImageAttach(event.currentTarget.files[0]);
      }
    },
    [onImageAttach],
  );

  const ctas = (
    <>
      <ImpressionWatcher
        active={trackImpressions}
        source={getSocialImpressionSource(
          IMPRESSION_PAGE,
          IMPRESSION_SECTION,
          'cancel',
        )}
      >
        <ActionButton onClick={onClose} disabled={loading}>
          Cancel
        </ActionButton>
      </ImpressionWatcher>
      <ImpressionWatcher
        active={trackImpressions}
        source={getSocialImpressionSource(
          IMPRESSION_PAGE,
          IMPRESSION_SECTION,
          'post',
        )}
      >
        <PrimaryButton disabled={!allowSubmit} onClick={submit}>
          {cta}
        </PrimaryButton>
      </ImpressionWatcher>
    </>
  );

  const wrappedFooter = footer ? (
    <ImpressionWatcher
      active={trackImpressions}
      source={getSocialImpressionSource(
        IMPRESSION_PAGE,
        IMPRESSION_SECTION,
        'info',
      )}
    >
      {footer}
    </ImpressionWatcher>
  ) : null;

  const textPrompt = (
    <ImpressionWatcher
      active={trackImpressions}
      source={getSocialImpressionSource(
        IMPRESSION_PAGE,
        IMPRESSION_SECTION,
        'add_text_prompt',
      )}
    >
      <Banner type="warning" icon={<InfoIcon />}>
        {image
          ? 'Write something about this image to help those who rely on text.'
          : 'Write something to explain why you are sharing this paper.'}
      </Banner>
    </ImpressionWatcher>
  );

  return (
    <Modal
      containerProps={{
        onKeyDown: closeModalOnEscape,
      }}
      onClose={closeModal}
    >
      <HeadingGroup>
        {mobile ? (
          ctas
        ) : (
          <>
            <Heading size="sm">{heading}</Heading>
            <CloseButton
              aria-label="Close"
              onClick={onClose}
              disabled={loading}
            >
              <CloseIcon color={Colors.GrayDark} />
            </CloseButton>
          </>
        )}
      </HeadingGroup>

      <Divider />

      <ContentContainer>
        <UserCard {...author} />

        {prompt && textPrompt}

        <ImpressionWatcher
          active={trackImpressions}
          source={getSocialImpressionSource(
            IMPRESSION_PAGE,
            IMPRESSION_SECTION,
            'text',
          )}
        >
          <SocialEditorContainer>
            <SocialEditor
              ref={socialEditorRef}
              content={content}
              tags={tags}
              placeholder={placeholder}
              disableTags={disableTags}
              disableHashtags={disableHashtags}
              onChange={({ content, tags }) => {
                setContent(content);
                setTags(tags);
              }}
              onSubmit={submit}
            />
          </SocialEditorContainer>
        </ImpressionWatcher>

        <ImpressionWatcher
          active={trackImpressions}
          source={getSocialImpressionSource(
            IMPRESSION_PAGE,
            IMPRESSION_SECTION,
            'attachment',
          )}
        >
          {attachmentContent}
        </ImpressionWatcher>

        {children}
      </ContentContainer>

      {mobile && (!disableImages || !disablePapers || wrappedFooter) && (
        <Divider />
      )}

      <ButtonRow>
        <ButtonGroup>
          {!disableImages && (
            <>
              <AttachButton
                aria-label="Image"
                disabled={
                  submitting || attachmentStatus !== AttachmentStatus.Empty
                }
                icon={<ImageIcon />}
                onClick={() => imageInputRef.current?.click()}
              >
                {!mobile && 'Image'}
              </AttachButton>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAddImage}
              />
            </>
          )}
          {!disablePapers && (
            <AttachButton
              aria-label="Paper"
              disabled={
                submitting || attachmentStatus !== AttachmentStatus.Empty
              }
              icon={<PaperIcon />}
              onClick={() => onPaperAttach()}
            >
              {!mobile && 'Paper'}
            </AttachButton>
          )}
        </ButtonGroup>

        {mobile ? wrappedFooter : <ButtonGroup>{ctas}</ButtonGroup>}
      </ButtonRow>

      {!mobile && wrappedFooter && (
        <>
          <Divider />
          {wrappedFooter}
        </>
      )}
    </Modal>
  );
};

export default CreateSocialItem;
